import * as React from "react";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

import { HotelImageCardProps } from "components/flexComponents/Hotels/typings";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";
import { useLocalization } from "@shared-ui/localization-context";

const HotelCard = (props: HotelImageCardProps) => {
  const { hotel, model, context } = props;
  const { callToAction, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  return (
    <UitkCard>
      <UitkCardContentSection>
        <UitkLayoutGrid columns={3} space="three">
          <UitkLayoutGridItem colSpan={1}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} className="uitk-card-roundcorner-all">
              <LazyLoadedImage src={hotelImageUrl} alt={hotel.hotelName} context={context} />
            </UitkFigure>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem colSpan={2}>
            <UitkLayoutFlex direction="column">
              <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
                {hotelName}
              </UitkHeading>
              <UitkRating rating={hotelStarRating} icon={ratingIcon(context.locale)} className={context.site.brand} />
              <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
            </UitkLayoutFlex>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <UitkLayoutFlex justifyContent="space-between">
          <UitkLayoutFlex direction="column">
            <ValueProposition hotel={hotel} className="all-b-margin-four" />
            <DistanceOrRating model={model} hotel={hotel} context={context} />
          </UitkLayoutFlex>
          <UitkLayoutFlex className="uitk-type-right">
            <PriceLockup hotel={hotel} priceA11y={new HotelPriceLockup(hotel, formatText)} hasGetRatesText />
          </UitkLayoutFlex>
        </UitkLayoutFlex>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <DescriptionOrReview model={model} hotel={hotel} />
        <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
      </UitkCardContentSection>

      <UitkCardLink>
        <TrackedLink
          data-testid={`hotelcard-link-${hotel.hotelName}`}
          aria-label={hotelName}
          moduleName="hotels"
          rfrr="card"
          rel={getHotelLinkRel(callToAction)}
          href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
        />
      </UitkCardLink>
    </UitkCard>
  );
};

export default HotelCard;
